import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
  type ComponentProps,
} from 'react';
import { Box } from '@mui/material';
import { Slider } from '@figma/fpl-components';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { FlexCenHorVer, FlexColCenVer } from '@/UI/styles';
import { clampValue } from '@/utils/numbers';
import { useIsHovered } from '@/hooks/useIsHovered';

type ScrubberProps = {
  time: number;
  duration: number;
  setTime: (time: number, ongoing: boolean) => void;
  playheadHeight?: number;
};

type SliderChangeHandler = NonNullable<ComponentProps<typeof Slider>['onChange']>;

function calculateTimeFromClick(clientX: number, rect: DOMRect, duration: number): number {
  const clickX = clientX - rect.left;
  const progress = clampValue(clickX / rect.width, 0, 1);
  const newTime = progress * duration;
  return newTime;
}

function getSliderStep(duration: number): number {
  return duration > 0 ? Math.min(0.01, duration) : 0.01;
}

function getSliderBigStep(duration: number, step: number): number {
  if (duration <= 0) {
    return 0.1;
  }

  return Math.max(step, Math.floor(Math.min(0.1, duration) / step) * step);
}

function formatTimeValue(time: number): string {
  const minutes = Math.floor(time / 60);
  const seconds = (time % 60).toFixed(2).padStart(5, '0');

  return `${String(minutes).padStart(2, '0')}:${seconds}`;
}

function roundToScrubberPrecision(time: number): number {
  return Math.round(time * 100) / 100;
}

function LegacyScrubber({ time, duration, setTime, playheadHeight = 16 }: ScrubberProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const progress = duration > 0 ? (time / duration) * 100 : 0;
  containerRef.current?.style.setProperty('--progress', `${progress}%`);

  // scrubbing
  const [dragging, setDragging] = useState<DOMRect>();
  const handleScrubStart = useCallback(
    (evt: ReactMouseEvent) => {
      const rect = evt.currentTarget.getBoundingClientRect();
      setDragging(rect);

      const newTime = calculateTimeFromClick(evt.clientX, rect, duration);
      setTime(newTime, true);
    },
    [setTime, duration],
  );
  useEffect(() => {
    function handleScrub(evt: MouseEvent) {
      if (!dragging) return;
      const newTime = calculateTimeFromClick(evt.clientX, dragging, duration);
      setTime(newTime, true);
    }

    function handleScrubEnd(evt: MouseEvent) {
      if (!dragging) return;
      const newTime = calculateTimeFromClick(evt.clientX, dragging, duration);
      setTime(newTime, false);
      setDragging(undefined);
    }

    document.addEventListener('mousemove', handleScrub);
    document.addEventListener('mouseup', handleScrubEnd);

    return () => {
      document.removeEventListener('mousemove', handleScrub);
      document.removeEventListener('mouseup', handleScrubEnd);
    };
  }, [dragging, setTime, duration]);

  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();

  return (
    <FlexCenHorVer ref={containerRef} sx={{ width: '100%' }} className="nodrag " data-testid="scrubber-wrapper">
      <FlexColCenVer
        sx={{
          width: '100%',
          marginLeft: 0.5,
          marginRight: 0.5,
          height: 16,
          position: 'relative',
          cursor: 'pointer',
        }}
        onMouseDown={handleScrubStart}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {/* progress bar */}
        <Box
          sx={{
            borderRadius: 2,
            height: 4,
            background: `linear-gradient(90deg,
               ${color.White100} 0%, ${color.White100} var(--progress, 0%), 
               ${color.White40_T} var(--progress, 0%), ${color.White40_T} 100%)`,
          }}
        />
        {/* scrubber handle */}
        <Box
          sx={{
            width: 2,
            height: playheadHeight,
            borderRadius: 2,
            background: color.White100,

            position: 'absolute',
            transform: 'translate(-50%, -50%)',
            top: '50%',
            left: `var(--progress, 0%)`,
          }}
        />
        {/* scrubber handle hover */}
        {(isHovered || dragging) && (
          <Box
            sx={{
              width: 12,
              height: 28,
              borderRadius: 6,
              background: dragging ? color.White40_T : token.bgSecondary,

              position: 'absolute',
              transform: 'translate(-50%, -50%)',
              top: '50%',
              left: `var(--progress, 0%)`,
            }}
          />
        )}
      </FlexColCenVer>
    </FlexCenHorVer>
  );
}

function FplScrubber({ time, duration, setTime }: ScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const pointerDraggingRef = useRef(false);
  const hasValidDuration = Number.isFinite(duration) && duration > 0;
  const safeDuration = hasValidDuration ? duration : 0;
  const safeTime = hasValidDuration && Number.isFinite(time) ? clampValue(time, 0, safeDuration) : 0;
  const step = useMemo(() => getSliderStep(safeDuration), [safeDuration]);
  const bigStep = useMemo(() => getSliderBigStep(safeDuration, step), [safeDuration, step]);

  const calculateTimeFromPointer = useCallback(
    (clientX: number): number | undefined => {
      const track = trackRef.current;
      if (!track || !hasValidDuration) {
        return undefined;
      }

      return calculateTimeFromClick(clientX, track.getBoundingClientRect(), safeDuration);
    },
    [hasValidDuration, safeDuration],
  );

  const handleSliderChange: SliderChangeHandler = useCallback(
    (sliderValue, { event, commit }) => {
      if (!hasValidDuration) {
        return;
      }

      if (event.type === 'keydown') {
        setTime(roundToScrubberPrecision(clampValue(sliderValue, 0, safeDuration)), false);
        return;
      }

      if (event.type === 'blur') {
        return;
      }

      if (!('clientX' in event)) {
        return;
      }

      const newTime = calculateTimeFromPointer(event.clientX);
      if (newTime === undefined) {
        return;
      }

      if (commit) {
        if (!pointerDraggingRef.current) {
          return;
        }

        pointerDraggingRef.current = false;
        setTime(newTime, false);
        return;
      }

      pointerDraggingRef.current = true;
      setTime(newTime, true);
    },
    [calculateTimeFromPointer, hasValidDuration, safeDuration, setTime],
  );

  return (
    <FlexCenHorVer sx={{ width: '100%' }} className="nodrag " data-testid="scrubber-wrapper">
      <FlexColCenVer
        ref={trackRef}
        sx={{
          width: '100%',
          marginLeft: 0.5,
          marginRight: 0.5,
          height: 16,
          justifyContent: 'center',
        }}
      >
        <Slider
          aria-label="Media timeline"
          size="sm"
          min={0}
          max={safeDuration}
          step={step}
          bigStep={bigStep}
          value={safeTime}
          valueText={formatTimeValue(safeTime)}
          rangeAnchor={0}
          disabled={!hasValidDuration}
          onChange={handleSliderChange}
        />
      </FlexColCenVer>
    </FlexCenHorVer>
  );
}

export const Scrubber = (props: ScrubberProps) => {
  const isFplMigrationEnabled = useFplMigrationFlag();

  if (isFplMigrationEnabled === true) {
    return <FplScrubber {...props} />;
  }

  return <LegacyScrubber {...props} />;
};
